import { createColumnHelper, RowModel, Table } from "@tanstack/react-table";

import { IVehicle, PartsStatusValues } from "../../models/Vehicle";
import openInNewTab from "../../utils/openInNewTab";
import AwaitingInventoryStatusDropdown from "../awaitingInventory/AwaitingInventoryStatusDropdown";
import PartsPriorityDropdown from "../awaitingInventory/PartsPriorityDropdown";
import YardStatusDropdown from "../awaitingInventory/YardStatusDropdown";
import PagedTable from "../shared/Tables/PagedTable";
import VehicleIcons from "../shared/VehicleIcons/VehicleIcons";
import VehicleComplexityDropdown from "./VehicleComplexityDropdown";
import VehicleLocationDropdown from "./VehicleLocationDropdown";
import PartVehicleDetails from "../leads/LeadParts/PartVehicleDetails";
import { act, HTMLProps, useEffect, useMemo, useRef, useState } from "react";
import { BanknotesIcon, CameraIcon, ChevronRightIcon, TruckIcon, UserIcon } from "@heroicons/react/24/outline";
import ClickToCopy from "../shared/ClickToCopy";
import UnlistedKTypeUpdate from "../ebayListings/unlistedTable/UnlistedKTypeUpdate";
import PaymentStatusCell from "../payments/PaymentStatusCell";
import { PageMode } from "../../models/PagedTable";
import LeadStatusCell from "../leads/LeadStatusCell";
import VehicleCodCell from "./VehicleCodCell";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";
import PartVehicleImages from "../leads/LeadParts/LeadImagesCarousel";
import VehicleJobsTableCell from "./VehicleJobsTableCell";
import ExportOffersTableRow from "../exports/ExportOffersTableRow";
import TableVehicleDetails from "./VehiclesTable/TableVehicleDetails";
import TableVehicleContact from "./VehiclesTable/TableVehicleContact";
import VehicleCollectedCODStatus from "../vehiclesCollected/VehicleCollectedCODStatus";
import VehiclePagedTable from "../shared/Tables/VehiclePagedTable";
import { LeadStatus, PaymentStatus } from "../../models/Lead";
import Tooltip from "../shared/Tooltip";
import ButtonGroup from "../shared/ButtonGroup";
import VehicleConfirmationModal, { IBulkResponse, IBulkVehicleAction } from "./VehicleConfirmationModal";

import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useLeads from "../../data/useLeads";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import usePayments from "../../data/usePayments";
import useEmails from "../../data/useEmails";
import Avatar from "../user/Avatar";
import VehicleOrders from "./VehicleOrders";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";
import BulkAddToWorkOrderModal from "../workOrders/BulkAddToWorkOrderModal";
import { SearchPaletteItem } from "../shared/Inputs/SearchPaletteTypes";
import LeadCheckInStatusCell from "../leads/LeadTable/TableSubComponents/LeadCheckInStatusCell";

function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!)

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate
    }
  }, [ref, indeterminate])

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + 'p-3 cursor-pointer border-gray-400'}
      {...rest}
    />
  )
}

const colHelper = createColumnHelper<IVehicle>();


type VehicleTableProps = {
  additionalBody?: Record<string, unknown>;
};

export default function VehicleTableTme({ additionalBody }: VehicleTableProps) {
  const [selectedVehicles, setSelectedVehicles] = useState<IVehicle[]>([]);
  const [selectedAction, setSelectedAction] = useState<IBulkVehicleAction | null>(null);
  const [isBulkWorkOrderOpen, setIsBulkWorkOrderOpen] = useState(false);
  const [workOrderItems, setWorkOrderItems] = useState<SearchPaletteItem[]>([]);

  const { saveHandlers } = useDefaultCRUDHandlers("Vehicles");
  const { bulkComplete, bulkSubmitCod } = useLeads(true, "VehicleTable");
  const { bulkMarkCollected } = useVehiclesMutations();
  const { bulkSendPayments } = usePayments();
  const {
    BulkSendDocumentsEmails,
    BulkSendDocumentRequestEmails,
    BulkSendBookingConfirmationEmails
  } = useEmails();


  function executeAction(body: any, mutation: any): Promise<IBulkResponse[]> {
    return new Promise<IBulkResponse[]>((resolve, reject) => {
      mutation.mutate(body, {
        onSuccess: (res: any) => {
          saveHandlers.onSuccess();
          resolve(res);
        },
        onError: (res: any) => {
          saveHandlers.onError();
          resolve([]);
        }
      });
    });
  }

  function executeLeadAction(body: any, mutation: any, vehicles: IVehicle[]): Promise<IBulkResponse[]> {
    return new Promise<IBulkResponse[]>((resolve, reject) => {
      mutation.mutate(body, {
        onSuccess: (res: any) => {
          saveHandlers.onSuccess();
          // returns lead ids, so map back to vehicles
          resolve(res.map((bulkResponse: IBulkResponse) => {
            return {
              ...bulkResponse,
              data: vehicles.find(v => v.leads[0]?.id === bulkResponse.data)?.id || ""
            }
          }));
        },
        onError: (res: any) => {
          saveHandlers.onError();
          resolve([]);
        }
      });
    });
  }

  // #region Vehicle Actions
  const vehicleActions: IBulkVehicleAction[] = [
    {
      title: "Mark Vehicles as Collected",
      action: async (vehicles: IVehicle[]) => {
        const body = {
          vehicleIds: vehicles.map(v => v.id).filter(id => id !== undefined) as string[]
        }

        return executeAction(body, bulkMarkCollected);
      },
      buttonText: "Mark as Collected"
    },
    // --------------------------- //
    {
      title: "Auto Complete Vehicles",
      body: "This will raise a payment for this lead and attempt to COD the vehicle.",
      action: async (vehicles: IVehicle[]) => {
        const body = {
          leads: vehicles.map(v => v.leads[0]?.id).filter(id => id !== undefined) as string[]
        }

        return executeLeadAction(body, bulkComplete, vehicles);

      },
      buttonText: "Auto Complete"
    },
    // --------------------------- //
    {
      title: "Submit COD for Vehicles",
      action: async (vehicles: IVehicle[]) => {
        const body = {
          leads: vehicles.map(v => v.leads[0]?.id).filter(id => id !== undefined) as string[]
        }

        return executeLeadAction(body, bulkSubmitCod, vehicles);
      },
      buttonText: "Submit COD"
    },
    // --------------------------- //
    {
      title: "Request Bank Details & ID for Vehicles",
      action: async (vehicles: IVehicle[]) => {
        const body = {
          leadIds: vehicles.map(v => v.leads[0]?.id).filter(id => id !== undefined) as string[]
        }
        return executeLeadAction(body, BulkSendDocumentRequestEmails, vehicles);
      },
      buttonText: "Request Bank Details & ID"
    },
    // --------------------------- //
    {
      title: "Raise Payment for Vehicles",
      action: async (vehicles: IVehicle[]) => {
        const body = {
          leadIds: vehicles.map(v => v.leads[0]?.id).filter(id => id !== undefined) as string[]
        }

        return executeLeadAction(body, bulkSendPayments, vehicles);
      },
      buttonText: "Raise Payment"
    },
    // --------------------------- //
    {
      title: "Send Attachments for Vehicles",
      action: async (vehicles: IVehicle[]) => {
        const body = {
          leadIds: vehicles.map(v => v.leads[0]?.id).filter(id => id !== undefined) as string[]
        }

        return executeLeadAction(body, BulkSendDocumentsEmails, vehicles);
      },
      buttonText: "Send Attachments"
    },
    // --------------------------- //
    {
      title: "Resend Confirmation for Vehicles",
      action: async (vehicles: IVehicle[]) => {
        const body = {
          leadIds: vehicles.map(v => v.leads[0]?.id).filter(id => id !== undefined) as string[]
        }

        return executeLeadAction(body, BulkSendBookingConfirmationEmails, vehicles);
      },
      buttonText: "Resend Confirmation"
    },
    // --------------------------- //
    // TODO: Implement Review Request as per Adam and Ben's feedback
    // {
    //   title: "Request Review for Vehicles",
    //   action: async (vehicles: IVehicle[]) => { return [] },
    //   buttonText: "Request Review"
    // },
  ];
  // #endregion

  // #region Expanded Row
  const expandedRow = (vehicle: IVehicle) => {
    return (
      <div className="flex flex-col gap-y-2">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-4 bg-gray-50 p-2 rounded-lg border border-gray-300">
          <ButtonGroup
            options={vehicleActions.map((groupedButton, index) => ({
              id: index.toString(),
              label: groupedButton.buttonText,
              value: groupedButton.buttonText, // Not used in action mode, but required by interface
              disabled: false,
              onClick: () => {
                setSelectedVehicles([vehicle]);
                setSelectedAction(groupedButton);
              },
            }))}
            actionMode={true}
            size="md"
          />
        </div>

        <PartVehicleDetails vehicle={vehicle} />

        {/* TEMP FIX TO AVOID STUPID WIDTH */}
        <div className="!max-w-[85vw]">
          <PartVehicleImages lead={vehicle.leads[0]} />
        </div>

        <VehicleOrders vehicleId={vehicle.id} />
      </div>
    )
  };
  // #endregion

  // #region Columns
  const cols = useMemo(
    () => [
      TableCheckboxCol(true, true),
      colHelper.accessor("vrm", {
        header: "Details",
        id: "details",
        sortingFn: "auto",
        cell: (info) => <TableVehicleDetails vehicle={info.row.original!} />,
      }),
      colHelper.accessor("contactId", {
        header: "Customer",
        id: "details",
        sortingFn: "auto",
        cell: (info) => <div>
          <TableVehicleContact contact={info.row.original.leads[0]?.contact} />
          <div className="flex items-start justify-between gap-4 mt-4">
            {info.row.original.vehicleImagesCount > 0 && (
              <Tooltip content="Vehicle Images">
                <div className="relative flex items-center justify-center gap-2">
                  <TruckIcon className="w-6 h-6 text-white bg-primary-600 p-1 rounded-full" />
                  <div className="font-semibold text-primary-600">{info.row.original.vehicleImagesCount}</div>
                </div>
              </Tooltip>
            )}

            {info.row.original.customerImagesCount > 0 && (
              <Tooltip content="Customer Images">
                <div className="relative flex items-center justify-center gap-2">
                  <UserIcon className="w-6 h-6 text-white bg-primary-600 p-1 rounded-full" />
                  <div className="font-semibold text-primary-600">{info.row.original.customerImagesCount}</div>
                </div>
              </Tooltip>
            )}
          </div>
        </div>
      }),
      colHelper.accessor("vehicleStarting", {
        header: "Vehicle",
        enableSorting: false,
        cell: (info) =>
          <div className="flex flex-col gap-y-2 w-24">
            <span>{PriceFormat(info.row.original.leads[0]?.price)}</span>
            <VehicleIcons vehicle={info.row.original!} />
            {info.row.original.leads[0]?.driverId && (
              <Avatar
                tooltipPrefix="Assigned Driver"
                userId={info.row.original.leads[0]?.driverId}
                classes="w-8 h-8 rounded-full [&:not(:first-child)]:-ml-2"
              />
            )}
          </div>,
      }),
      colHelper.display({
        header: "Vehicle Status",
        cell: (info) => <div className="flex flex-col space-y-4">
          <span></span>
          {info.row.original.leads?.length > 0 && (
            <>
              <LeadStatusCell status={info.row.original.leads[0].status} />
              <PaymentStatusCell lead={info.row.original.leads[0]} />
              {/* <VehicleCodCell vehicle={info.row.original} /> */}
              <VehicleCollectedCODStatus leadId={info.row.original.leads[0].id} vehicle={info.row.original} classes="" />
            </>
          )}
        </div>,
      }),
      colHelper.accessor("checkInId", {
        header: "Check-In",
        id: "checkInStatus",
        enableSorting: false,
        cell: (info) => <LeadCheckInStatusCell checkInId={info.getValue()} />,
      }),
      colHelper.display({
        header: "Jobs",
        enableSorting: false,
        cell: (info) => <VehicleJobsTableCell lead={info.row.original.leads[0]} vehicle={info.row.original} />,
      }),
      colHelper.display({
        header: "Export Offers",
        enableSorting: false,
        cell: (info) => <ExportOffersTableRow vehicle={info.row.original} />,
      }),
      colHelper.accessor("engineExportStatus", {
        header: "Part Status",
        enableSorting: false,
        cell: (info) => <div id={info.row.original.id}>
          <p>Parts Status</p>
          <AwaitingInventoryStatusDropdown vehicle={info.row.original!} classes="h-8 w-40 text-sm" />
          <p className="mt-2">Parts Priority Status</p>
          <PartsPriorityDropdown vehicle={info.row.original!} classes="h-8 w-40 text-sm" />
        </div>
      }),
      colHelper.accessor("yardStatus", {
        header: "Yard Status",
        enableSorting: false,
        cell: (info) => <div id={info.row.original.id}>
          <p className="mt-2">Yard Location</p>
          <VehicleLocationDropdown vehicle={info.row.original!} size="sm" classes="h-8 w-40 text-sm" />
          <p className="mt-2">Yard Status</p>
          <YardStatusDropdown vehicle={info.row.original!} classes="h-8 w-40 text-sm" />
          <p className="mt-2">Complexity</p>
          <VehicleComplexityDropdown vehicle={info.row.original!} classes="h-8 w-40 text-sm" />
        </div>
      }),
      colHelper.accessor("created", {
        header: "Created",
        enableSorting: false,
        cell: (info) => <span>{formatDateTime(info.getValue())}</span>
      }),
    ],
    []
  );
  // #endregion

  // #region Render
  return (
    <>
      <VehicleConfirmationModal
        vehicles={selectedVehicles}
        bulkAction={selectedAction}
        open={selectedAction !== null}
        setOpen={(open: boolean) => {
          if (!open) {
            setSelectedAction(null);
            setSelectedVehicles([]);
          }
        }}
      />
      <BulkAddToWorkOrderModal
        open={isBulkWorkOrderOpen}
        onClose={() => {
          setIsBulkWorkOrderOpen(false);
          setWorkOrderItems([]);
        }}
        selectedItems={workOrderItems}
      />

      <VehiclePagedTable
        columns={cols}
        url="/vehicle"
        pageMode={PageMode.Stock}// not using this
        queryLabel="Search vehicles VRM, Stock Number, VIN, Make, Model, Engine Code or Location Name"
        expandedRow={(vehicle) => expandedRow(vehicle)}
        onRowClick={(x: IVehicle) => openInNewTab(`vehicles/${x.id}`)}
        filters={[
          { id: "1", label: "All", value: {}, type: "searchBody", additionalBodyKey: "isCollected", default: true },
          { id: "2", label: "Awaiting Collection", value: { isCollected: false }, type: "searchBody" },
          { id: "3", label: "Collected", value: { isCollected: true }, type: "searchBody" },
          { id: "4", label: "Check-In", value: { isCollected: true, engineExportStatus: PartsStatusValues["Awaiting Schedule"] }, type: "searchBody" },
          { id: "5", label: "Needs Payment", value: { lead: { paymentStatus: PaymentStatus["Not Started"] } }, type: "searchBody" },
          { id: "6", label: "COD", value: { codConfirmed: false, codRequired: true }, type: "searchBody" },
          { id: "7", label: "Complete", value: { lead: { status: LeadStatus.Completed } }, type: "searchBody" },
        ]}
        actionButtons={[
          {
            label: "Bulk Actions",
            onClick: () => { },
            groupedButtons: vehicleActions.length > 0 ? vehicleActions.map((action) => ({
              label: action.buttonText,
              disabledOnNoRows: true,
              onClick: (rows: RowModel<IVehicle>, table: Table<IVehicle>) => {
                setSelectedVehicles(table.getSelectedRowModel().flatRows.map(row => row.original));
                setSelectedAction(action);
              },
            })).concat([
              {
                label: "Add to work order",
                disabledOnNoRows: true,
                onClick: (_rows: RowModel<IVehicle>, table: Table<IVehicle>) => {
                  const selected = table.getSelectedRowModel().flatRows.map((row) => row.original);
                  const items: SearchPaletteItem[] = selected
                    .filter((vehicle) => Boolean(vehicle.id))
                    .map((vehicle) => ({
                      id: vehicle.id!,
                      name: `${vehicle.vrm ?? "Unknown VRM"}${vehicle.combinedMake ? ` - ${vehicle.combinedMake}` : ""}${vehicle.combinedModel ? ` ${vehicle.combinedModel}` : ""}`,
                      type: "Vehicle",
                    }));

                  setWorkOrderItems(items);
                  setIsBulkWorkOrderOpen(true);
                },
              },
            ]) : [],
          }
        ]}
        additionalBody={{
          includeParts: true,
          includePartImages: true,
          includeCatalyticResults: true,
          includeExportOffers: true,
          ...(additionalBody ?? {})
        }}
      />
    </>
  );
  // #endregion
}
